import React from 'react';
import { Box, Modal, Fade, TableRow, TableCell, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RadarChart, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Text } from 'recharts';
import styled from '@emotion/styled';

interface OwnProps {
    isOpen: boolean,
    data: any[],
    fiscalYear: string,
    handleCloseChart: any
}

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '850px',
    height: '500px',
    bgcolor: 'background.paper',
    textAlign: 'center',
    borderRadius: '15px'
};

const ModalLabel = styled(Box)(({ }) => ({
    backgroundColor: '#41DBCE',
    fontSize: '1.2rem',
    color: '#FFFFFF',
    height: '2rem',
    paddingTop: '0.5rem',
    borderRadius: '15px 15px 0 0',
    textAlign: 'left',
    paddingLeft: '1rem'
}));

const BottomRight = styled.span`
    position: absolute;
    bottom: 15%;
    right: 35%;
`;

const ColumnTable = styled(Box)(({ }) => ({
    position: 'absolute',
    right: '5%',
    top: '20%'
}));

const ColumnTableRow = styled(TableRow)(({ }) => ({
    '& .MuiTableCell-root': {
        border: '1px solid black',
        height: '2rem',
        fontSize: '0.8rem',
        paddingTop: '0',
        paddingBottom: '0',
    }
}));

// 各ラベルの位置調整用定義
const labelPositionMap: { [key: string]: { [key: string]: number } } = {
    '流動比率': {
        'x': 0,
        'y': 0
    },
    '固定比率': {
        'x': -20,
        'y': 0
    },
    '棚卸資産回転日数': {
        'x': -5,
        'y': 0
    },
    '総資本回転日数': {
        'x': 5,
        'y': 0
    },
    '売上高経常利益率': {
        'x': 0,
        'y': 0
    },
    '売上高総利益率': {
        'x': 0,
        'y': 0
    },
    '1人当たり月売上高': {
        'x': 0,
        'y': 0
    },
    '1人当たり月経常利益': {
        'x': -20,
        'y': 0
    },
}

// レーダーチャートの角度（回転させない場合は0）
const raderAngle = 0

// ラベル描画用コンポーネント
const CustomTick = (props: any) => {
    const { payload, cx, cy, } = props
    const angleRad = -(payload.coordinate * Math.PI) / 180;
    // 各ラベルの描画位置を調整する
    const labelPositionX = labelPositionMap[payload.value] ? labelPositionMap[payload.value].x : 0
    const labelPositionY = labelPositionMap[payload.value] ? labelPositionMap[payload.value].y : 0
    const x = cx + labelPositionX + 210 * Math.cos(angleRad);
    const y = cy + labelPositionY + 150 * Math.sin(angleRad);
    return (
        <Text
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={16}
        >
            {payload.value}
        </Text>
    );
};

// 目盛り線、放物線の描画用コンポーネント
const CustomGrid = ({ cx, cy, radiusSteps, angles }: { cx: number, cy: number, radiusSteps: number[], angles: number[] }) => {
    const maxRadius = radiusSteps[radiusSteps.length - 1];
    return (
        <>
            {radiusSteps.map((r, i) => {
                const points = angles.map((angle) => {
                    const rad = ((angle + raderAngle) * Math.PI) / 180;
                    const x = cx + r * Math.cos(rad);
                    const y = cy + r * Math.sin(rad);
                    return `${x}, ${y}`;
                }).join(' ');
                return (
                    <polygon
                        points={points}
                        stroke={i % 2 === 0 ? '#696969' : '#000000'}
                        strokeWidth={i % 2 === 0 ? 1 : 2}
                        strokeDasharray={i % 2 === 0 ? '4 4' : ''}
                        fill='none'
                    />
                );
            })}
            {angles.map((angle, i) => {
                const rad = ((angle + raderAngle) * Math.PI) / 180;
                const x = cx + maxRadius * Math.cos(rad);
                const y = cy + maxRadius * Math.sin(rad);
                return (
                    <line
                        key={`radial-${i}`}
                        x1={cx}
                        y1={cy}
                        x2={x}
                        y2={y}
                        stroke="#696969"
                        strokeWidth={1}
                        strokeDasharray=""
                    />
                );
            })}
        </>
    );
};

const RadarChartModal = (props: OwnProps) => {
    const { isOpen, data, fiscalYear, handleCloseChart } = props
    const [open, setOpen] = React.useState(isOpen);
    React.useEffect(() => {
        setOpen(isOpen)
    }, [isOpen])

    const scaleNum = 4; // 目盛の数
    const outerRadius = 130; // レーダーチャートの外半径
    const radiusSteps = Array.from({ length: scaleNum }, (_, i) => outerRadius * (i + 1) / scaleNum);
    const angles = Array.from({ length: data.length }, (_, i) => (360 / data.length) * i);

    return (
        <>
            <Modal
                open={open}
                onClose={handleCloseChart}
                closeAfterTransition
            >
                <Fade in={open}>
                    <Box sx={modalStyle}>
                        <ModalLabel>
                            財務分析チャート
                            <IconButton onClick={handleCloseChart} sx={{ position: 'absolute', top: '1px', right: '1rem' }}>
                                <CloseIcon style={{ color: '#fff' }} />
                            </IconButton>
                        </ModalLabel>
                        <Box>
                            <BottomRight><span style={{ color: '#1111DD' }}>■</span>{fiscalYear}年</BottomRight>
                            <ColumnTable>
                                <ColumnTableRow>
                                    <TableCell rowSpan={2} sx={{ backgroundColor: '#B5F9E8' }}>安全性</TableCell>
                                    <TableCell>流動比率</TableCell>
                                </ColumnTableRow>
                                <ColumnTableRow>
                                    <TableCell>固定比率</TableCell>
                                </ColumnTableRow>
                                <ColumnTableRow>
                                    <TableCell rowSpan={2} sx={{ backgroundColor: '#B5F9E8' }}>効率性</TableCell>
                                    <TableCell>棚卸資産回転日数</TableCell>
                                </ColumnTableRow>
                                <ColumnTableRow>
                                    <TableCell>総資本回転日数</TableCell>
                                </ColumnTableRow>
                                <ColumnTableRow>
                                    <TableCell rowSpan={2} sx={{ backgroundColor: '#B5F9E8' }}>収益性</TableCell>
                                    <TableCell>売上高経常利益率</TableCell>
                                </ColumnTableRow>
                                <ColumnTableRow>
                                    <TableCell>売上高総利益率</TableCell>
                                </ColumnTableRow>
                                <ColumnTableRow>
                                    <TableCell rowSpan={2} sx={{ backgroundColor: '#B5F9E8' }}>生産性</TableCell>
                                    <TableCell>1人当たり月売上高</TableCell>
                                </ColumnTableRow>
                                <ColumnTableRow>
                                    <TableCell>1人当たり月経常利益</TableCell>
                                </ColumnTableRow>
                            </ColumnTable>
                        </Box>
                        <ResponsiveContainer width="100%" height="80%">
                            <RadarChart startAngle={90 - raderAngle} endAngle={-270 - raderAngle} cx="35%" cy="50%" outerRadius="130" data={data}>
                                <CustomGrid cx={297.5} cy={200} radiusSteps={radiusSteps} angles={angles} />
                                <PolarAngleAxis dataKey="column" tick={<CustomTick />} />
                                <PolarRadiusAxis angle={90 + raderAngle} domain={[0, 200]} />
                                <Radar legendType='none' dataKey={(e) => Math.min(e.value, 200)} stroke="#8884d8" fill="#1111DD" fillOpacity={0.8} strokeWidth={1} />
                            </RadarChart>
                        </ResponsiveContainer>
                        ※チャート上のメモリ100は業種平均値を表しており、目盛200が最大です。
                    </Box>
                </Fade>
            </Modal>
        </>
    );
}

export default RadarChartModal;
